<template>
  <f7-page>
    <f7-navbar :title="$t('tontine.title')" back-link />
    <AppLayout>
      <template #main>
    <f7-block v-if="loading" strong inset>
      <f7-preloader /> {{ $t('tontine.loading') }}
    </f7-block>

    <template v-else-if="plan">
      <!-- Cycle parameters -->
      <f7-block-title>{{ $t('tontine.rules.cycleSection') }}</f7-block-title>
      <f7-list inset strong>
        <f7-list-item :title="$t('tontine.rules.contribution')" :after="money(plan.contributionAmount)" />
        <f7-list-item :title="$t('tontine.rules.frequency')" :after="frequencyLabel" />
        <f7-list-item v-if="plan.customFrequencyDays" :title="$t('tontine.rules.customDays')" :after="String(plan.customFrequencyDays)" />
        <f7-list-item :title="$t('tontine.rules.firstDate')" :after="formatDate(plan.firstContributionDate)" />
        <f7-list-item :title="$t('tontine.rules.gracePeriod')" :after="`${plan.gracePeriodDays || 0} ${$t('tontine.rules.days')}`" />
        <f7-list-item :title="$t('tontine.rules.lateFee')" :after="money(plan.latePaymentFee)" />
      </f7-list>

      <!-- Allocation -->
      <f7-block-title>{{ $t('tontine.rules.allocationSection') }}</f7-block-title>
      <f7-block strong inset>
        <h2>{{ allocationLabel }}</h2>
        <p class="rules-copy">{{ allocationExplanation }}</p>
        <p class="text-color-gray">{{ $t('tontine.rules.allocationFooter') }}</p>
      </f7-block>

      <!-- Governance -->
      <f7-block-title>{{ $t('tontine.rules.governanceSection') }}</f7-block-title>
      <f7-list inset strong>
        <f7-list-item :title="$t('tontine.rules.payoutApprovals')" :after="String(plan.requiredPayoutApprovals)" />
        <f7-list-item :title="$t('tontine.rules.members')" :after="String(plan.memberCount)" />
        <f7-list-item :title="$t('tontine.rules.beneficiaryVerification')" :after="plan.beneficiaryVerified ? $t('tontine.rules.verified') : $t('tontine.rules.pending')" />
      </f7-list>
      <f7-block strong inset class="info-notice">
        <p class="text-color-gray">
          <strong>{{ $t('tontine.rules.dualControlTitle') }}</strong><br>
          {{ $t('tontine.rules.dualControlBody') }}
        </p>
      </f7-block>

      <!-- Visibility -->
      <f7-block-title>{{ $t('tontine.rules.visibilitySection') }}</f7-block-title>
      <f7-block strong inset>
        <h2>{{ visibilityLabel }}</h2>
        <p class="rules-copy">{{ visibilityExplanation }}</p>
      </f7-block>

      <!-- Mode -->
      <f7-block-title>{{ $t('tontine.rules.modeSection') }}</f7-block-title>
      <f7-block strong inset>
        <h2>{{ modeLabel }}</h2>
        <p class="rules-copy">{{ modeExplanation }}</p>
        <p v-if="plan.paymentPartnerCode" class="text-color-gray">
          {{ $t('tontine.rules.partnerCode', { code: plan.paymentPartnerCode }) }}
        </p>
      </f7-block>

      <!-- Payout workflow -->
      <f7-block-title>{{ $t('tontine.rules.payoutWorkflowTitle') }}</f7-block-title>
      <f7-list inset strong>
        <f7-list-item :title="$t('tontine.rules.step1')" />
        <f7-list-item :title="$t('tontine.rules.step2')" />
        <f7-list-item :title="$t('tontine.rules.step3')" />
        <f7-list-item :title="$t('tontine.rules.step4')" />
      </f7-list>

      <!-- Regulatory -->
      <f7-block strong inset class="info-notice">
        <p class="text-color-gray">
          <strong>{{ $t('tontine.infoNoticeTitle') }}</strong><br>
          {{ $t('tontine.infoNoticeBody') }}
        </p>
      </f7-block>
    </template>
         </template>
    </AppLayout>
  </f7-page>
</template>

<script setup>
import { computed, onMounted } from 'vue'
import { f7 } from 'framework7-vue'
import { useI18n } from 'vue-i18n'
import { useTontineStore } from '@/js/pinia-stores/tontine'
import { useKeycloakStore } from '@/js/pinia-stores/keycloak'
import AppLayout from "../../components/common/AppLayout.vue";


const { t } = useI18n()
const props = defineProps({ campaignId: String, groupId: String })
const store = useTontineStore()
const keycloak = useKeycloakStore()

const plan = computed(() => store.plan)
const loading = computed(() => store.loading)

const frequencyLabel = computed(() => ({
  WEEKLY: t('tontine.frequency.WEEKLY'),
  BIWEEKLY: t('tontine.frequency.BIWEEKLY'),
  MONTHLY: t('tontine.frequency.MONTHLY'),
  QUARTERLY: t('tontine.frequency.QUARTERLY'),
  CUSTOM: t('tontine.frequency.CUSTOM'),
}[plan.value?.frequency] || ''))
const allocationLabel = computed(() => ({
  FIXED_ROTATION: t('tontine.allocation.FIXED_ROTATION'),
  RANDOM_DRAW: t('tontine.allocation.RANDOM_DRAW'),
  MEMBER_VOTE: t('tontine.allocation.MEMBER_VOTE'),
  COMMITTEE_APPROVAL: t('tontine.allocation.COMMITTEE_APPROVAL'),
  NEED_BASED: t('tontine.allocation.NEED_BASED'),
}[plan.value?.allocationMethod] || ''))
const modeLabel = computed(() => ({
  COORDINATION_ONLY: t('tontine.mode.COORDINATION_ONLY'),
  PARTNER_PAYMENT_ORCHESTRATED: t('tontine.mode.PARTNER_PAYMENT_ORCHESTRATED'),
  REGULATED_PARTNER_SAVINGS: t('tontine.mode.REGULATED_PARTNER_SAVINGS'),
}[plan.value?.operatingMode] || ''))
const visibilityLabel = computed(() => ({
  MEMBER_TOTALS_ONLY: t('tontine.visibility.MEMBER_TOTALS_ONLY'),
  MEMBERS_SEE_NAMES_NOT_AMOUNTS: t('tontine.visibility.MEMBERS_SEE_NAMES_NOT_AMOUNTS'),
  MEMBERS_SEE_NAMES_AND_AMOUNTS: t('tontine.visibility.MEMBERS_SEE_NAMES_AND_AMOUNTS'),
  FINANCE_ADMINS_ONLY: t('tontine.visibility.FINANCE_ADMINS_ONLY'),
}[plan.value?.financialVisibility] || ''))

const allocationExplanation = computed(() => {
  const key = plan.value?.allocationMethod
  if (key === 'RANDOM_DRAW') return t('tontine.rules.allocExplain.RANDOM_DRAW')
  if (key === 'MEMBER_VOTE') return t('tontine.rules.allocExplain.MEMBER_VOTE')
  if (key === 'COMMITTEE_APPROVAL') return t('tontine.rules.allocExplain.COMMITTEE_APPROVAL')
  if (key === 'NEED_BASED') return t('tontine.rules.allocExplain.NEED_BASED')
  return t('tontine.rules.allocExplain.FIXED_ROTATION')
})
const visibilityExplanation = computed(() => {
  const key = plan.value?.financialVisibility
  if (key === 'MEMBERS_SEE_NAMES_NOT_AMOUNTS') return t('tontine.rules.visExplain.MEMBERS_SEE_NAMES_NOT_AMOUNTS')
  if (key === 'MEMBERS_SEE_NAMES_AND_AMOUNTS') return t('tontine.rules.visExplain.MEMBERS_SEE_NAMES_AND_AMOUNTS')
  if (key === 'FINANCE_ADMINS_ONLY') return t('tontine.rules.visExplain.FINANCE_ADMINS_ONLY')
  return t('tontine.rules.visExplain.MEMBER_TOTALS_ONLY')
})
const modeExplanation = computed(() => {
  const key = plan.value?.operatingMode
  if (key === 'PARTNER_PAYMENT_ORCHESTRATED') return t('tontine.rules.modeExplain.PARTNER_PAYMENT_ORCHESTRATED')
  if (key === 'REGULATED_PARTNER_SAVINGS') return t('tontine.rules.modeExplain.REGULATED_PARTNER_SAVINGS')
  return t('tontine.rules.modeExplain.COORDINATION_ONLY')
})

onMounted(load)
async function load() {
  try {
    await store.loadSummary(props.campaignId)
  } catch (err) {
    f7.dialog.alert(err.response?.data?.message || err.message || t('tontine.loadError'))
  }
}

function money(v, c) {
  return new Intl.NumberFormat(keycloak.locale || 'fr', {
    style: 'currency',
    currency: c || plan.value?.currency || 'XOF',
    maximumFractionDigits: 0,
  }).format(v || 0)
}
function formatDate(v) {
  if (!v) return '—'
  return new Intl.DateTimeFormat(keycloak.locale || 'fr', { day: 'numeric', month: 'long', year: 'numeric' }).format(
    new Date(v.startsWith('20') && v.length === 10 ? v + 'T00:00:00' : v)
  )
}
</script>

<style scoped>
.rules-copy { font-size: 14px; line-height: 1.6; margin: 8px 0 0; }
h2 { margin: 0; font-size: 20px; }
.info-notice p { font-size: 13px; line-height: 1.5; margin: 0; }
</style>